// Capitalizes each words in `str` string, and converts non-alphanumeric characters into spaces.
// Example:
//   titleize("foo_bar-baz")
// returns:
//   "Foo Bar Baz"
import dayjs from 'dayjs';
export function titleize(str) {
  if (!str) return '';
  return str
    .replace(/[^0-9A-Za-z]/g, ' ')
    .split(' ')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}

// Flattens a nested error structure into an array of strings.
// Example:
//   flattenErrors({ user: { email: 'is required', password: 'cannot be too short' } })
// returns:
//   ['User Email is required', 'User Password cannot be too short']
export function flattenErrors(errors, prefix = '') {
  if (Array.isArray(errors)) {
    return errors.flatMap((err) => flattenErrors(err, prefix));
  }
  if (typeof errors === 'object') {
    return Object.entries(errors).flatMap(([key, err]) =>
      flattenErrors(err, `${prefix}${titleize(key)} `)
    );
  }
  return prefix ? `${prefix}${errors}` : errors;
}

// Extract errors from standard API response structure under the given resource name using the given extractor function.
// Returns the count of errors extracted.
// If the errors is an Array rather than a nested Object structure, it will be treated as an array of `base` errors.
// Example:
//   extractErrors(errors, 'user', setFieldError)
export function extractErrors(errors, resourceName, fn) {
  let count = 0;
  if (errors) {
    if (Array.isArray(errors)) {
      if ((count += errors.length)) {
        fn('base', errors);
      }
    } else if ((errors = errors[resourceName])) {
      for (const k in errors) {
        const errs = errors[k];
        count += errs.length;
        fn(k, errs);
      }
    }
  }
  return count;
}

// Convenience function for use with Formik <Field> components
// to compute the `isInvalid` prop of Chakra UI <FormControl> components.
// Example:
//   <Field name="email">
//     {({ field, form, meta }) => (
//       <FormControl isInvalid={isInvalidField(form, meta)}>
export function isInvalidField(form, meta) {
  return meta?.error && (meta?.touched || form?.submitCount);
}
// this is for shared emails, we need to pass null, when choosing non shared email user

export function convertToFormData(payload) {
  const formData = new FormData();

  function appendFormData(key, value) {
    // File/Blob must come before the generic `object` branch: `Object.entries(new File(...))` is [] so
    // nested shapes like `{ file: File, filename: string }` would otherwise drop the binary.
    if (value instanceof File) {
      formData.append(key, value, value?.name ?? '');
      return;
    }
    if (value instanceof Blob) {
      formData.append(key, value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (typeof item === 'object' && item !== null) {
          if (item instanceof File) {
            formData.append(`${key}[]`, item, item?.name ?? '');
          } else if (item instanceof Blob) {
            formData.append(`${key}[]`, item);
          } else {
            appendFormData(`${key}[]`, item);
          }
        } else {
          formData.append(`${key}[]`, item);
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      Object.entries(value).forEach(([subKey, subValue]) => {
        appendFormData(`${key}[${subKey}]`, subValue);
      });
    } else if (value !== null && value !== undefined) {
      formData.append(key, value);
    }
  }

  Object.entries(payload).forEach(([key, value]) => appendFormData(key, value));

  return formData;
}

export function hexToRgbAdjusted(hex, opacity = 1) {
  // Remove the '#' if present
  hex = hex.replace(/^#/, '');

  // Parse the hex values
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  if (opacity === 1) {
    return `rgb(${r}, ${g}, ${b})`;
  } else if (opacity >= 0 && opacity <= 1) {
    // Blend with white to simulate opacity
    const blendedR = Math.round(r * opacity + 255 * (1 - opacity));
    const blendedG = Math.round(g * opacity + 255 * (1 - opacity));
    const blendedB = Math.round(b * opacity + 255 * (1 - opacity));
    return `rgb(${blendedR}, ${blendedG}, ${blendedB})`;
  } else {
    return `rgb(${r}, ${g}, ${b})`; // Return original if opacity is invalid.
  }
}

/**
 * Formats a start and end date into a readable MM/YYYY string.
 *
 * Display logic:
 * - Both start and end provided → "MM/YYYY - MM/YYYY"
 * - Only start provided → "MM/YYYY - Present"
 * - Only end provided → "MM/YYYY"
 * - Neither provided → "-"
 *
 * @param {string } startDate - The start date string (ISO format or valid date string)
 * @param {string } endDate - The end date string (ISO format or valid date string)
 * @returns {string} A formatted date range string
 */
export function formatStartAndEndDates(startDate, endDate) {
  if (startDate && endDate) {
    return `${dayjs(startDate).format('MM/YYYY')} - ${dayjs(endDate).format(
      'MM/YYYY'
    )}`;
  }
  if (startDate && !endDate) {
    return `${dayjs(startDate).format('MM/YYYY')} - Present`;
  }
  if (!startDate && endDate) {
    return `${dayjs(endDate).format('MM/YYYY')}`;
  }
  return '-';
}

export function getFirstApplicationReviewStage(stages, ATS_MILESTONES) {
  if (!stages?.length) return null;

  const appliedStages = stages.filter(
    (stage) => stage?.ats_job_milestone?.name === ATS_MILESTONES.APPLIED
  );

  return appliedStages.reduce(
    (minStage, currentStage) =>
      !minStage || currentStage.position < minStage.position
        ? currentStage
        : minStage,
    null
  );
}
